import {
	Button,
	EmptyState,
	Input,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@ondit/ui";
/**
 * Which pages the answers leaned on, and how many of them were yours.
 *
 * Citation share is the headline; underneath it, the same links counted three
 * ways. By kind, because "what sort of thing does the model trust here" decides
 * what to do — a category that is mostly directories is one where getting
 * listed works. By domain and by page, because the site is who to talk to and
 * the page is what to write. And a watchlist, because "is the page I published
 * last week being cited yet" is the only one of these questions with a date in
 * it, and it is answered by a row that starts at zero.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bookmark, FileText, Globe, PieChart, Plus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useAccess, whyDisabled } from "~/core/access/index";
import { ApiError, api, type CitedRow } from "~/core/api/index";
import * as fmt from "~/core/format";
import { useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { ConfirmRemoval } from "~/core/ui/confirm-removal";
import { Section } from "~/core/ui/page";
import { Async } from "~/core/ui/states";
import { MetricPanel, Series } from "~/modules/analytics/components/chart";
import { Drill } from "~/modules/analytics/components/detail";
import { InsightIcon } from "~/modules/analytics/components/insight-icon";
import { type ReadingProps, StageShell } from "~/modules/analytics/components/stage";
import { matches, SortHeader, TableTools, useSort } from "~/modules/analytics/components/table";

type Column = "key" | "count" | "with_mention";

export function InsightCitations({ entityId, filters, setFilters }: ReadingProps) {
	const t = useT();
	const state = useQuery({
		queryKey: [
			...keys.entity(entityId),
			"insights",
			"citations",
			{
				range: filters.range,
				engine: filters.engine,
				locale: filters.locale,
				topics: filters.topics,
				intent: filters.intent,
				category: filters.category,
				tag: filters.tag,
			},
		],
		queryFn: () => api.insights.citations(entityId, filters),
		...read,
	});

	return (
		<StageShell
			entityId={entityId}
			reading="citations"
			filters={filters}
			setFilters={setFilters}
			tags={state.data?.tags}
		>
			<Async state={state}>
				{(data) => (
					<div className="insight-dashboard citations-dashboard">
						<MetricPanel
							metric="citation_share"
							title={t("metric.citation_share")}
							lede={t("metric.citation_share.hint")}
							block={data.citation_share}
							rankLabel={t("insights.brand")}
							chart={
								<Series
									metric="citation_share"
									points={data.citation_share.series}
									assets={data.citation_share.assets}
								/>
							}
						/>

						<Mix rows={data.mix} picked={filters.category ?? ""} onPick={(category) => setFilters({ category })} />

						<Tabs defaultValue="domains" className="citation-lists">
							<TabsList aria-label={t("citations.sources")}>
								<TabsTrigger value="domains">
									<InsightIcon tone="detect">
										<Globe />
									</InsightIcon>
									{t("citations.domainsView")}
								</TabsTrigger>
								<TabsTrigger value="pages">
									<InsightIcon tone="analyse">
										<FileText />
									</InsightIcon>
									{t("citations.pagesView")}
								</TabsTrigger>
								<TabsTrigger value="watched">
									<InsightIcon tone="generate">
										<Bookmark />
									</InsightIcon>
									{t("citations.monitored")}
								</TabsTrigger>
							</TabsList>
							<TabsContent value="domains">
								<CitedTable
									title={t("citations.topDomains")}
									lede={t("citations.topDomainsHint")}
									rows={data.domains}
									by="domain"
									name={`citation-domains-${filters.range}`}
								/>
							</TabsContent>
							<TabsContent value="pages">
								<CitedTable
									title={t("citations.topPages")}
									lede={t("citations.topPagesHint")}
									rows={data.pages}
									by="url"
									name={`citation-pages-${filters.range}`}
								/>
							</TabsContent>
							<TabsContent value="watched">
								<Monitored entityId={entityId} rows={data.monitored} />
							</TabsContent>
						</Tabs>
					</div>
				)}
			</Async>
		</StageShell>
	);
}

/**
 * The mix, as a row of counts that is also the filter.
 *
 * The shape of the list matters more than any single row in it, and the next
 * question after "mostly forums" is always "which ones" — so clicking a
 * category filters to it, and clicking it again clears it. A filter you cannot
 * switch off is a trap.
 */
function Mix({
	rows,
	picked,
	onPick,
}: {
	rows: { category: string; count: number; share: number }[];
	picked: string;
	onPick: (category: string) => void;
}) {
	const t = useT();
	const { locale } = useI18n();
	if (rows.length === 0) return null;

	return (
		<Section
			title={t("citations.mix")}
			icon={
				<InsightIcon tone="analyse">
					<PieChart />
				</InsightIcon>
			}
			lede={t("citations.mixLede")}
		>
			<div className="mix">
				{rows.map((row) => (
					<Button
						variant="default"
						key={row.category}
						type="button"
						className={`h-auto flex-col items-start justify-start whitespace-normal p-(--s-8) text-left ${picked === row.category ? "mix-cell active" : "mix-cell"}`}
						aria-pressed={picked === row.category}
						onClick={() => onPick(picked === row.category ? "" : row.category)}
					>
						<span className="label">{t(`citations.kind.${row.category}`)}</span>
						<span className="value num">{fmt.percent(locale, row.share, 1)}</span>
						<span className="foot num">{row.count}</span>
					</Button>
				))}
			</div>
		</Section>
	);
}

export function CitedTable({
	title,
	lede,
	rows,
	by,
	name,
}: {
	title: string;
	lede: string;
	rows: CitedRow[];
	by: "domain" | "url";
	name: string;
}) {
	const t = useT();
	const { locale } = useI18n();
	const [query, setQuery] = useState("");
	const [expanded, setExpanded] = useState(false);
	const sort = useSort<Column>("count");

	const shown = sort.apply(
		rows.filter((row) => matches(query, row.key, row.title, ...row.tags)),
		(row, column) => (column === "key" ? row.key : column === "count" ? row.count : row.with_mention),
	);

	return (
		<Section title={title} lede={lede}>
			<TableTools
				sort={sort.config([
					{ id: "key", label: by === "domain" ? t("table.domain") : t("table.page") },
					{ id: "with_mention", label: t("citations.withMention") },
					{ id: "count", label: t("citations.count") },
				])}
				query={query}
				onQuery={setQuery}
				shown={shown.length}
				total={rows.length}
				csv={{ name, rows: csv(shown, by) }}
			/>

			{shown.length === 0 ? (
				<EmptyState size="compact" title={t("table.noMatch")} />
			) : (
				<div className="table-scroll">
					<Table variant="resource">
						<TableHeader>
							<TableRow>
								<TableHead className="num">#</TableHead>
								<SortHeader
									className="wrap"
									label={by === "domain" ? t("table.domain") : t("table.page")}
									column="key"
									sort={sort}
								/>
								<TableHead>{t("citations.kind")}</TableHead>
								<SortHeader className="num" label={t("citations.withMention")} column="with_mention" sort={sort} />
								<SortHeader className="num" label={t("citations.count")} column="count" sort={sort} />
								<TableHead className="num">{t("citations.share")}</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{(expanded ? shown : shown.slice(0, 10)).map((row) => (
								<TableRow key={row.key}>
									<TableCell className="num mono faint">{row.rank}</TableCell>
									<TableCell className="wrap">
										{/* Grouped by domain the row is a site, and what a reader
										    wants next is who they are; grouped by URL it is a
										    page, and what they want is the answers that linked to
										    it. Two questions, two drawers. */}
										<Drill
											view={
												by === "domain"
													? { kind: "rival", label: row.key, domain: row.key }
													: { kind: "citations", label: row.title ?? row.key, filters: { url: row.key } }
											}
										>
											{row.key}
										</Drill>
										{row.tags.map((tag) => (
											<span className="pill pill-quiet" key={tag}>
												{tag}
											</span>
										))}
									</TableCell>
									<TableCell>
										<span className={kindClass(row.category)}>{t(`citations.kind.${row.category}`)}</span>
									</TableCell>
									{/* The gap between these two columns is the finding: a link
									    the answer spent that did not put your name in it. */}
									<TableCell className="num mono">{row.with_mention}</TableCell>
									<TableCell className="num mono">
										<Drill
											view={{
												kind: "citations",
												label: row.title ?? row.key,
												filters: by === "domain" ? { domain: row.key } : { url: row.key },
											}}
										>
											{row.count}
										</Drill>
									</TableCell>
									<TableCell className="num mono">{fmt.percent(locale, row.share, 1)}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}
			{shown.length > 10 ? (
				<div className="citation-list-footer">
					<span className="faint num">
						{t("table.showing", { shown: expanded ? shown.length : 10, total: shown.length })}
					</span>
					<Button variant="ghost" onClick={() => setExpanded(!expanded)}>
						{t(expanded ? "insights.collapse" : "insights.expand")}
					</Button>
				</div>
			) : null}
		</Section>
	);
}

/**
 * The watchlist.
 *
 * A page nothing has cited stays in the table with a zero, because that row is
 * the reason somebody opened this section. `first_cited_at` is backfilled from
 * the whole history when the page is added, so it is a date about the page
 * rather than about when somebody thought to watch it.
 */
function Monitored({ entityId, rows }: { entityId: string; rows: CitedRow[] }) {
	const t = useT();
	const { locale } = useI18n();
	const { editor, why } = useAccess();
	const [url, setUrl] = useState("");
	const [busy, setBusy] = useState(false);
	const queryClient = useQueryClient();
	const watch = useMutation({
		mutationFn: (page: string) => api.addMonitoredPage(entityId, { url: page }),
		onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) }),
	});

	async function add(event: React.FormEvent) {
		event.preventDefault();
		setBusy(true);
		try {
			await watch.mutateAsync(url);
			setUrl("");
		} catch (e) {
			toast.error(e instanceof ApiError ? e.localized(locale) : String(e));
		} finally {
			setBusy(false);
		}
	}

	return (
		<Section title={t("citations.monitored")} lede={t("citations.monitoredHint")}>
			<form className="row-actions" onSubmit={add} style={{ marginBottom: "var(--s-8)" }}>
				<Input
					className="flex-1 min-w-72"
					type="url"
					aria-label={t("citations.monitored")}
					placeholder="https://example.com/pricing"
					value={url}
					onChange={(event) => setUrl(event.target.value)}
					required
					disabled={!editor}
					title={whyDisabled(editor, why.editor)}
				/>
				<Button type="submit" disabled={busy || !editor || !url.trim()} title={whyDisabled(editor, why.editor)}>
					<Plus aria-hidden />
					{t("citations.watch")}
				</Button>
			</form>

			{rows.length === 0 ? (
				<EmptyState size="compact" title={t("citations.noneWatched")} description={t("citations.noneWatchedBody")} />
			) : (
				<div className="table-scroll">
					<Table variant="resource">
						<TableHeader>
							<TableRow>
								<TableHead className="wrap">{t("table.page")}</TableHead>
								<TableHead>{t("citations.kind")}</TableHead>
								<TableHead className="num">{t("citations.withMention")}</TableHead>
								<TableHead className="num">{t("citations.count")}</TableHead>
								<TableHead>{t("citations.firstCited")}</TableHead>
								<TableHead />
							</TableRow>
						</TableHeader>
						<TableBody>
							{rows.map((row) => (
								<TableRow key={row.key}>
									<TableCell className="wrap">
										<Drill view={{ kind: "citations", label: row.key, filters: { url: row.key } }}>{row.key}</Drill>
										{row.tags.map((tag) => (
											<span className="pill pill-quiet" key={tag}>
												{tag}
											</span>
										))}
									</TableCell>
									<TableCell>
										<span className={kindClass(row.category)}>{t(`citations.kind.${row.category}`)}</span>
									</TableCell>
									<TableCell className="num mono">{row.with_mention}</TableCell>
									<TableCell className="num mono">{row.count}</TableCell>
									<TableCell className="mono">
										{row.first_cited_at ? (
											fmt.date(locale, row.first_cited_at)
										) : (
											<span className="faint">{t("citations.notYet")}</span>
										)}
									</TableCell>
									<TableCell className="num">
										<Remove entityId={entityId} url={row.key} />
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}
		</Section>
	);
}

/**
 * Unwatching, which needs the row's id and the table only has its URL.
 *
 * Looked up rather than returned on the row: the citations tab counts links,
 * and putting the watchlist's primary key on a row that is otherwise a count
 * would be carrying an id through three layers for one button.
 */
function Remove({ entityId, url }: { entityId: string; url: string }) {
	const t = useT();
	const { locale } = useI18n();
	const { editor, why } = useAccess();
	const [busy, setBusy] = useState(false);
	const queryClient = useQueryClient();
	const unwatch = useMutation({
		mutationFn: async () => {
			const pages = await api.monitoredPages(entityId);
			const found = pages.find((page) => page.url === url);
			if (found) await api.removeMonitoredPage(entityId, found.id);
		},
		onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) }),
	});

	async function remove() {
		setBusy(true);
		try {
			await unwatch.mutateAsync();
		} catch (e) {
			toast.error(e instanceof ApiError ? e.localized(locale) : String(e));
		} finally {
			setBusy(false);
		}
	}

	return (
		<span title={whyDisabled(editor, why.editor)}>
			<ConfirmRemoval label={`${t("common.remove")} · ${url}`} onConfirm={remove} disabled={busy || !editor} />
		</span>
	);
}

function kindClass(category: string): string {
	if (category === "yours") return "pill pill-success";
	if (category === "ondit") return "pill pill-accent";
	if (category === "competitor") return "pill pill-warning";
	return "pill";
}

function csv(rows: CitedRow[], by: "domain" | "url"): (string | number)[][] {
	return [
		[by, "title", "kind", "tags", "cited", "with_mention", "share_percent"],
		...rows.map((row) => [
			row.key,
			row.title ?? "",
			row.category,
			row.tags.join(" "),
			row.count,
			row.with_mention,
			row.share.toFixed(2),
		]),
	];
}
